import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import DramaCard from '@/pages/home/components/DramaCard';
import { dramas, categoryMap } from '@/mocks/dramas';
import type { Drama } from '@/mocks/dramas';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const [drama, setDrama] = useState<Drama | null>(null);
  const [activeTab, setActiveTab] = useState<'episodes' | 'info'>('episodes');

  useEffect(() => {
    window.scrollTo(0, 0);
    const found = dramas.find((d) => d.id === id);
    setDrama(found || null);
    setActiveTab('episodes');
  }, [id]);

  if (!drama) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="text-foreground-500 text-sm mb-4">剧集未找到</p>
            <Link to="/" className="inline-block px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedDramas = dramas
    .filter(
      (d) =>
        d.id !== drama.id &&
        (d.category === drama.category || d.tags.some((t) => drama.tags.includes(t)))
    )
    .slice(0, 6);

  const categoryName = categoryMap[drama.category] || drama.subCategory;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${drama.title} - 桃子影院在线观看`,
              description: drama.description,
              url: `https://www.taozitv.com/detail/${drama.id}`,
              author: { '@type': 'Person', name: drama.director },
              datePublished: `${drama.year}-01-01`,
              publisher: { '@type': 'Organization', name: '桃子影院' },
            }),
          }}
        />

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 bg-background-100/50 border-b border-background-200/30">
          <div className="max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <span className="text-foreground-700">/</span>
              <Link to={`/category/${drama.category}`} className="hover:text-primary-400 transition-colors whitespace-nowrap">
                {categoryName}
              </Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">{drama.title}</span>
            </nav>
          </div>
        </div>

        {/* Drama Info */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Poster */}
              <div className="w-full md:w-[300px] shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={drama.coverImage}
                    alt={drama.title}
                    title={`${drama.title} - 桃子影院在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mb-3">
                  {drama.title}
                </h1>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="px-3 py-1 rounded-full bg-accent-500/15 text-accent-400 text-sm font-medium whitespace-nowrap">
                    {drama.rating} 分
                  </span>
                  <span className="text-foreground-500 text-sm">{drama.year}</span>
                  <span className="text-foreground-700">|</span>
                  <span className="text-foreground-500 text-sm">{drama.region}</span>
                  <span className="text-foreground-700">|</span>
                  <span className="text-foreground-500 text-sm">{drama.subCategory}</span>
                  <span className="text-foreground-700">|</span>
                  <span className={`text-sm whitespace-nowrap ${drama.status === '连载中' ? 'text-primary-400' : 'text-foreground-400'}`}>
                    {drama.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {drama.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                  {drama.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground-600 shrink-0">导演：</span>
                    <span className="text-foreground-300">{drama.director}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground-600 shrink-0">语言：</span>
                    <span className="text-foreground-300">{drama.language}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground-600 shrink-0">主演：</span>
                    <span className="text-foreground-300">{drama.cast.join(' / ')}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground-600 shrink-0">集数：</span>
                    <span className="text-foreground-300">
                      {drama.status === '连载中'
                        ? `更新至第${drama.currentEpisode}集 / 共${drama.totalEpisodes}集`
                        : `共${drama.totalEpisodes}集全`}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground-600 shrink-0">更新：</span>
                    <span className="text-foreground-300">{drama.updateTime}</span>
                  </div>
                </div>

                {/* Play button */}
                <button className="mt-6 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-fill"></i>
                  </span>
                  立即播放第1集
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Episode List & Info Tabs */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12 bg-background-100/50">
          <div className="max-w-[1200px] mx-auto">
            {/* Tab Switcher */}
            <div className="flex items-center gap-1 mb-6 bg-background-200/50 rounded-full p-1 w-fit">
              <button
                onClick={() => setActiveTab('episodes')}
                className={`px-5 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'episodes'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-200'
                }`}
              >
                分集列表
              </button>
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-200'
                }`}
              >
                剧集信息
              </button>
            </div>

            {activeTab === 'episodes' && (
              <div>
                <h2 className="text-lg font-heading text-foreground-200 mb-4">
                  分集列表（共{drama.totalEpisodes}集）
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                  {drama.episodes.map((ep) => (
                    <button
                      key={ep.num}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/40 hover:border-primary-500/30 hover:bg-primary-500/5 transition-all cursor-pointer text-left group"
                    >
                      <span className="text-foreground-400 text-sm group-hover:text-primary-400 transition-colors">
                        {ep.title}
                      </span>
                      <span className="text-foreground-600 text-xs">{ep.duration}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'info' && (
              <div className="max-w-2xl">
                <h2 className="text-lg font-heading text-foreground-200 mb-4">剧集信息</h2>
                <div className="space-y-4 text-sm">
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">导演</span>
                    <span className="text-foreground-300">{drama.director}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">主演</span>
                    <span className="text-foreground-300">{drama.cast.join(' / ')}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">类型</span>
                    <span className="text-foreground-300">{drama.subCategory}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">地区</span>
                    <span className="text-foreground-300">{drama.region}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">语言</span>
                    <span className="text-foreground-300">{drama.language}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">年份</span>
                    <span className="text-foreground-300">{drama.year}</span>
                  </div>
                  <div className="flex items-baseline gap-3 pb-3 border-b border-background-200/30">
                    <span className="text-foreground-600 shrink-0 w-16">状态</span>
                    <span className="text-foreground-300">{drama.status}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-foreground-600 shrink-0 w-16">简介</span>
                    <p className="text-foreground-400 leading-relaxed">{drama.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Related */}
        {relatedDramas.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="max-w-[1200px] mx-auto">
              <h2 className="text-xl font-heading text-foreground-100 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedDramas.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}